import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React from 'react';
import useMedia from 'use-media';
import { PAGE } from '../../../../constants/pages';
import Button, { ButtonVariant } from '../../../Button';
import DownArrow from './DownArrow';
import UpArrow from './UpArrow';

interface MetricSelectorProps {
  feature?: any;
  page?: string;
}

const MetricSelector: React.FC<MetricSelectorProps> = props => {
  const { feature, page = 'homepage' } = props;
  const { t } = useTranslation();

  const images = useStaticQuery(graphql`
    query {
      digital: file(relativePath: { eq: "digital-shelf-insights.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      retail: file(relativePath: { eq: "retail-intelligence.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      total: file(relativePath: { eq: "total-sales.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
    }
  `);

  const metricImg = {
    'Retail Intelligence': images?.retail?.childImageSharp?.gatsbyImageData,
    'Digital Shelf Insights': images?.digital?.childImageSharp?.gatsbyImageData,
    'Total Sales Analytics': images?.total?.childImageSharp?.gatsbyImageData,
  };

  // scale 0.95
  const scale95 = useMedia({ maxWidth: '1340px', minWidth: '1300px' });
  const scale90 = useMedia({ maxWidth: '1300px', minWidth: '1225px' });
  const scale75 = useMedia({ maxWidth: '1225px' });

  const variantImage = feature
    ? metricImg[feature?.name]
    : images?.total?.childImageSharp?.gatsbyImageData;

  if (scale75 && page === 'homepage') {
    return (
      <div className="w-full">
        <GatsbyImage image={variantImage} alt="metrics" />
      </div>
    );
  } else if (page === PAGE.SELLICS_JOINS_PERPETUA) {
    return (
      <div className="w-full lg:w-11/12">
        <GatsbyImage image={variantImage} alt="metrics" />
      </div>
    );
  }

  return (
    <div
      className={classNames(
        'bg-white rounded-lg border border-gray-4-light shadow p-6',
        {
          'scale-95': scale95,
          'scale-90': scale90,
        }
      )}
      style={{ maxWidth: '600px' }}
    >
      <p className="text-sm font-semibold text-gray-2-light mb-4">
        {t('Edit Metrics')}
      </p>
      <div className="grid grid-cols-4 grid-rows-3 gap-4">
        {feature &&
          feature.options[0].data.map((item, index) => {
            return (
              <React.Fragment key={item.title}>
                <Metric
                  {...(item
                    ? {
                        title: item.title,
                        amount: item.amount,
                        changeAmount: item.changeAmount,
                        changeDirection: item.changeDirection,
                      }
                    : {})}
                />
                <Metric />
                <Metric />
              </React.Fragment>
            );
          })}
      </div>
      <div className="flex justify-end mt-6">
        <Button variant={ButtonVariant.primary} text={t('Done')} />
      </div>
    </div>
  );
};

export default MetricSelector;

enum ChangeDirection {
  up = 'up',
  down = 'down',
}

interface MetricProps {
  title?: string;
  amount?: string;
  changeAmount?: string;
  changeDirection?: ChangeDirection;
  className?: string;
}

const Metric: React.FC<MetricProps> = props => {
  const { title, amount, changeAmount, changeDirection, className } = props;

  const shouldShowContent = title && amount && changeAmount;
  return (
    <div
      className="relative"
      style={{
        minWidth: '121px',
        height: '80px',
        maxHeight: '80px',
      }}
    >
      <div
        className={classNames(
          'relative bg-white border border-gray-4-light rounded-lg min-w-min ease-out duration-300',
          { '-top-2 -left-4 z-10 shadow scale-110': title },
          className
        )}
      >
        {shouldShowContent && (
          <div className="p-3" style={{ width: '155px' }}>
            <p className="text-sm font-semibold mb-2">{title}</p>
            <p className="text-2xl font-semibold mb-1">{amount}</p>
            <div className="flex items-center text-sm font-medium text-green-1">
              <div className="flex items-center mr-2">
                {changeDirection === ChangeDirection.up ? (
                  <UpArrow />
                ) : (
                  <DownArrow />
                )}
              </div>
              {changeAmount}
            </div>
          </div>
        )}
        {!shouldShowContent && (
          <div className="p-4" style={{ width: '121px' }}>
            <div className="rounded-sm bg-gray-5-light w-10 h-2 mb-3" />
            <div className="rounded-sm bg-gray-5-light w-16 h-3 mb-4" />
            <div className="rounded-sm bg-gray-5-light w-10 h-2" />
          </div>
        )}
      </div>
    </div>
  );
};
